'use client';

import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import type { PageAffiliateOldDesignSchema } from '@/sanity/pages/PageAffiliateOldDesignTemplate';
import { motion } from 'framer-motion';
import DealsTableNew from './DealsTableNew';

export default function Deals({
  data,
}: {
  data: PageAffiliateOldDesignSchema;
}) {
  const dealsItems = data?.affiliatesTablesRef?.dealsItems;

  return (
    <section className="w-full lg:px-0 px-[2rem]">
      <div className="w-[min(230rem,100%)] mx-auto bg-[#103037] rounded-[1.5rem] lg:rounded-[5rem] px-[2rem] py-[3.75rem] lg:py-[16.25rem]">
        <div className="relative mx-auto w-[min(178.5rem,100%)]">
          <motion.div
            initial={{ opacity: 0, y: -40, filter: 'blur(10px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
          >
            {data?.dealsTitle && (
              <div className="mb-[5rem] richtext richtext-center prose-headings:text-white">
                <PortableText
                  value={data.dealsTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </motion.div>

          {dealsItems && dealsItems.length > 0 && (
            <div className="flex flex-col gap-[2.5rem] lg:gap-[1.5rem]">
              {dealsItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex relative min-h-[45rem] lg:p-[1rem] overflow-clip"
                  initial={{ opacity: 0, x: -50, filter: 'blur(10px)' }}
                  whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.1,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                >
                  <div
                    className="flex relative flex-col items-center w-full border lg:flex-row py-[2rem] px-[2rem] lg:pr-[5rem] rounded-[2.75rem] border-black/5 gap-[5rem] min-h-[45rem]"
                    style={{ backgroundColor: item.backgroundColor }}
                  >
                    <div
                      className="flex flex-col justify-between p-[2rem] lg:p-[3rem] lg:py-[4.5rem] rounded-[2rem] flex-shrink-0 w-[min(52.375rem,100%)] lg:self-stretch"
                      style={{ backgroundColor: item.backgroundColorTitle }}
                    >
                      <div>
                        {item.chip && (
                          <div className="mb-[2rem]">
                            <div className="rounded-full bg-[#103037] p-[1.125rem] w-fit">
                              <p className="text-white text-[1.75rem] leading-[100%] font-[500] m-0 uppercase">
                                {item.chip}
                              </p>
                            </div>
                          </div>
                        )}
                        {item.title && (
                          <h3 className="text-[#103037] text-[4.375rem] leading-[120%] font-[500] mb-[2rem]">
                            {item.title}
                          </h3>
                        )}
                      </div>

                      <div className="flex flex-col gap-[1.5rem]">
                        {item.tagsTitle && (
                          <p className="text-[#103037]/60 text-[1.75rem] leading-[135%] font-[500] uppercase">
                            {item.tagsTitle}
                          </p>
                        )}
                        {item.tags && item.tags.length > 0 && (
                          <div className="flex flex-wrap gap-[1rem]">
                            {item.tags.map((tag, tagIndex) => (
                              <div
                                key={tagIndex}
                                className="rounded-full bg-[#10303726] py-[0.5rem] px-[1.5rem]"
                              >
                                <p className="font-geist text-[2rem] m-0 leading-[125%] font-[400] text-[#17345D]">
                                  {tag.text}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                        {item?.callout && (
                          <div className="w-full mt-[3rem]">
                            <p className="text-[#103037] text-[2.25rem] m-0 font-[400] font-sf leading-[134%] tracking-[-0.0675rem]">
                              {item.callout}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col flex-1 justify-start py-[2rem] w-full">
                      <DealsTableNew
                        table={item.table}
                        dealIndex={index}
                        showSideBanner={item.showSideBanner}
                      />
                    </div>
                  </div>

                  {item?.showSideBanner && (
                    <div className="absolute top-[4.75rem] -right-[6.9rem] lg:top-[5.75rem] lg:-right-[6rem] rotate-45 flex items-center justify-center bg-[#6FD19A] h-[4.25rem] px-[5rem] z-10 drop-shadow-[0_5.353px_18.734px_rgba(0,0,0,0.25)]">
                      <div
                        className="absolute left-[.625rem] top-full h-0 w-0"
                        style={{
                          borderLeft: '.675rem solid #427762',
                          borderRight: '.675rem solid transparent',
                          borderTop: '.675rem solid #427762',
                          borderBottom: '.675rem solid transparent',
                        }}
                      />
                      <div
                        className="absolute right-[.75rem] top-full h-0 w-0"
                        style={{
                          borderRight: '.75rem solid #427762',
                          borderLeft: '.75rem solid transparent',
                          borderTop: '.75rem solid #427762',
                          borderBottom: '.75rem solid transparent',
                        }}
                      />
                      <p className="mb-0 leading-none tracking-tight text-[#103037] flex gap-[.75rem] items-center whitespace-nowrap">
                        <span className="font-semibold text-[1.75rem]">
                          {item.showSideBannerValueText ?? '10k just $1'}
                        </span>
                        <span className="text-[1.375rem] opacity-70">
                          {item.showSideBannerDescText ?? 'Start-Angebot'}
                        </span>
                      </p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
